import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ToolbarUIComponentFactory } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { LoomPlugin } from './loomPluginType';
import { insertLoom, recordVideo, setupLoom } from './pm-plugins/commands';
import { createPlugin, loomPluginKey } from './pm-plugins/main';
import { loomPrimaryToolbarComponent } from './ui/PrimaryToolbarButton';
import { getLoomQuickInsertComponents } from './ui/quick-insert/getLoomQuickInsertComponents';
import { getQuickInsertItem } from './ui/quickInsert';
import { getToolbarComponents } from './ui/toolbar-components';

export const loomPlugin: LoomPlugin = ({ config, api }) => {
	const editorAnalyticsAPI = api?.analytics?.actions;
	const isNewToolbarEnabled = Boolean(api?.toolbar);

	// Workaround since we want to insert a loom via the `hyperlink` plugin for now.
	// The hyperlink plugin (and the card plugin) are deeply tied into using the Prosemirror Command
	// Ideally one day we refactor those and we can remove this.
	const editorViewRef: Record<'current', EditorView | null> = { current: null };
	const primaryToolbarComponent: ToolbarUIComponentFactory = loomPrimaryToolbarComponent(
		config,
		api,
	);
	const isRegisteredSlashCommandEnabled = isExperimentEnabled('platform_editor_slash_command');
	let isLoomMenuItemRegistered = false;
	const registerLoomMenuItem = () => {
		if (!isRegisteredSlashCommandEnabled || isLoomMenuItemRegistered || !api?.uiControlRegistry) {
			return;
		}
		api.uiControlRegistry.actions.register(getLoomQuickInsertComponents({ api }));
		isLoomMenuItemRegistered = true;
	};
	if (config.loomProvider && isRegisteredSlashCommandEnabled) {
		registerLoomMenuItem();
	}
	if (isNewToolbarEnabled) {
		api?.toolbar?.actions.registerComponents(getToolbarComponents(config, api));
	} else {
		api?.primaryToolbar?.actions.registerComponent({
			name: 'loom',
			component: primaryToolbarComponent,
		});
	}

	return {
		name: 'loom',

		actions: {
			recordVideo,
			insertLoom: (video, positionType) =>
				insertLoom(editorViewRef.current, api, video, positionType),
			initLoom: async ({ loomProvider }) => {
				const result = await setupLoom(loomProvider, api, editorViewRef.current, true);
				if (!result.error) {
					registerLoomMenuItem();
				}
				return result;
			},
		},

		pmPlugins: () => [
			{
				name: 'loom',
				plugin: () => createPlugin({ config, api }),
			},
			{
				name: 'loomViewRefWorkaround',
				plugin: () => {
					return new SafePlugin({
						view: (editorView: EditorView) => {
							// Do not cleanup the editorViewRef on destroy
							// because some functions may point to a stale
							// reference and this means we will return null.
							// EditorView is assumed to be stable so we do not need to
							// cleanup.
							// See: #hot-106316
							editorViewRef.current = editorView;
							return {};
						},
					});
				},
			},
		],

		getSharedState(editorState) {
			if (!editorState) {
				return;
			}
			return loomPluginKey.getState(editorState);
		},

		pluginsOptions: isRegisteredSlashCommandEnabled
			? {}
			: {
					// Enable inserting Loom recordings through the slash command
					quickInsert: (intl) => {
						if (config.loomProvider) {
							return getQuickInsertItem(editorAnalyticsAPI)(intl);
						}
						return [];
					},
				},

		// Enable inserting Loom recordings through main toolbar
		primaryToolbarComponent:
			!api?.primaryToolbar && !isNewToolbarEnabled ? primaryToolbarComponent : undefined,
	};
};
